import Equalizer from "ui/templates/Game/MixPuzzle/MixPuzzle/atoms/Equalizer/Equalizer";
import PanKnob from "ui/templates/Game/MixPuzzle/MixPuzzle/atoms/PanKnob/PanKnob";
import Button from "ui/atoms/Button/Button";
import Panel from "./MixConsolePanel";
import prettyInstrument from "util/formatters/prettyInstrument";
import {
  isInstrumentTechniqueCorrect,
  isInstrumentCorrect,
} from "../../state/selectors";
import classNames from "classnames";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { State } from "../../state/reducer";
import Action from "../../state/actions";
import { MixPuzzleInstrument } from "shared/games/mixpuzzles/types";
import { difficultySettings } from "../../constants";
import Switch, { SwitchItem } from "ui/atoms/Switch/Switch";
import { Dispatch } from "react";
import MixPuzzleVolumeSlider from "../../atoms/MixPuzzleVolumeSlider";

interface Props {
  state: State;
  dispatch: Dispatch<Action>;
  instrument: MixPuzzleInstrument;
}

export default function MixConsoleReportCard({
  instrument,
  state,
  dispatch,
}: Props) {
  const isCorrect = isInstrumentCorrect(state, instrument.instrument);
  const compressionEnabled = difficultySettings.compression(state) === true;

  return (
    <div
      className={classNames("rounded p-1", {
        "bg-green-500": isCorrect,
        "bg-red-500": !isCorrect,
      })}
    >
      <div className={"flex items-center justify-between px-2 py-1 font-brik"}>
        <div>
          {prettyInstrument(instrument.instrument)}:{" "}
          {isCorrect ? "Correct" : "Incorrect"}
        </div>

        {isCorrect ? (
          <CheckCircleIcon className={"ml-2 h-5"} />
        ) : (
          <XCircleIcon className={"ml-2 h-5"} />
        )}
      </div>

      <div className={"bg-black p-2 text-sm"}>
        <div className={"mb-1 flex justify-center py-2"}>
          <Switch
            size={"large"}
            wholeButtonToggle
            value={state.playbackState}
            onChange={(payload) =>
              dispatch({ type: "updatePlaybackState", payload })
            }
            items={items}
          />
        </div>

        <div className={"grid gap-2"}>
          <Panel
            title={"Volume"}
            orientation={"horizontal"}
            bodyClassName={"p-2"}
            correct={isInstrumentTechniqueCorrect(
              state,
              instrument.instrument,
              "volume"
            )}
          >
            <MixPuzzleVolumeSlider value={instrument.volume} onChange={noop} />
          </Panel>

          <div className={"grid grid-cols-[1fr_100px] gap-2"}>
            <Panel
              title={"Equalizer"}
              orientation={"vertical"}
              bodyClassName={classNames(
                "relative",
                compressionEnabled ? "flex-1" : "h-52"
              )}
              correct={isInstrumentTechniqueCorrect(
                state,
                instrument.instrument,
                "eq"
              )}
            >
              <Equalizer
                value={instrument}
                onChange={noop}
                roundedClassName={"rounded-b"}
              />
            </Panel>

            <div className={"grid gap-2"}>
              <Panel
                title={"Pan"}
                orientation={"vertical"}
                correct={isInstrumentTechniqueCorrect(
                  state,
                  instrument.instrument,
                  "pan"
                )}
                bodyClassName={"p-2"}
              >
                <PanKnob value={instrument.pan} onChange={noop} />
              </Panel>

              {compressionEnabled && (
                <Panel
                  title={"Comp"}
                  orientation={"vertical"}
                  correct={isInstrumentTechniqueCorrect(
                    state,
                    instrument.instrument,
                    "comp"
                  )}
                  bodyClassName="grid gap-1 p-2 pb-1"
                >
                  <Button
                    theme="default"
                    block
                    active={instrument.comp === null}
                    onClick={noop}
                  >
                    Off
                  </Button>

                  <Button
                    theme="default"
                    block
                    active={instrument.comp === 7}
                    onClick={noop}
                  >
                    7 DB
                  </Button>
                </Panel>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const noop = () => {};

const items: SwitchItem<State["playbackState"]>[] = [
  { label: "Yours", value: "mix" },
  { label: "Answer", value: "reference" },
];
